import type { ServiceInfo } from "../lib/api.ts";
import { MeetingBadge } from "./MeetingBadge.tsx";

interface ServiceListProps {
  services: ServiceInfo[];
  selected: string[];
  onToggle: (id: string) => void;
}

function formatPrice(cents: number | null, currency: string): string {
  if (cents === null) return "Free";
  if (cents === 0) return "Free";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function ServiceList({ services, selected, onToggle }: ServiceListProps) {
  const selectedServices = services.filter((s) => selected.includes(s.id));
  const totalMin = selectedServices.reduce((sum, s) => sum + s.duration_min, 0);
  const totalCents = selectedServices.reduce((sum, s) => sum + (s.price_cents ?? 0), 0);
  const currency = selectedServices[0]?.currency ?? "USD";
  const hasPrice = selectedServices.some((s) => s.price_cents !== null && s.price_cents > 0);

  return (
    <div className="space-y-2">
      {services.map((service) => {
        const isSelected = selected.includes(service.id);
        return (
          <button
            key={service.id}
            type="button"
            onClick={() => onToggle(service.id)}
            className={`w-full text-left rounded-[var(--radius)] border-2 p-4 transition-all duration-150 bg-white ${
              isSelected
                ? "border-[var(--accent)] shadow-sm"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="flex items-start gap-3">
              {/* Checkbox indicator */}
              <div
                className={`mt-0.5 w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                  isSelected
                    ? "border-[var(--accent)] bg-[var(--accent)]"
                    : "border-gray-300 bg-white"
                }`}
              >
                {isSelected && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className={`font-semibold text-sm transition-colors ${
                    isSelected ? "text-[var(--accent)]" : "text-gray-900"
                  }`}
                >
                  {service.name}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500">{service.duration_min} min</span>
                  <MeetingBadge mode={service.meeting_mode} />
                </div>
              </div>

              <div className="shrink-0 text-right">
                <span className="text-sm font-bold text-gray-900">
                  {formatPrice(service.price_cents, service.currency)}
                </span>
              </div>
            </div>
          </button>
        );
      })}

      {/* Running total bar */}
      {selected.length > 0 && (
        <div
          className="mt-3 rounded-[var(--radius)] px-4 py-3 text-white text-sm flex items-center justify-between"
          style={{ backgroundColor: "var(--accent)" }}
        >
          <span>
            {selected.length} service{selected.length !== 1 ? "s" : ""} · {totalMin} min
          </span>
          {hasPrice && (
            <span className="font-semibold">{formatPrice(totalCents, currency)}</span>
          )}
        </div>
      )}
    </div>
  );
}
